import { http } from '@/api/http';
import { ApiRequestError } from '@/api/client';
import { parsePostalCodeList } from '@/api/modules/user';
import type { Order, OrderCleanItem, OrderServicerInfo, ProgressNode } from '@/types/order';
import { buildAdminCreatePrices } from '@/lib/orderPricing';
import { computeEqualDurationAllocations, listOrderAssignedStaff } from '@/lib/settleAllocation';
import { hasRememberedInvoiceSent } from '@/lib/invoiceSentOrders';
import { formatServiceTime } from '@/utils/formatServiceTime';
import {
  createEmptyCalendarStatusCounts,
  hasCalendarStatusCounts,
  type CalendarDayStatusCounts,
} from '@/constants/calendarStatus';

const OP_ORDER_LIST_PATH = '/system/team/op/myorders';
const TEAM_ORDER_LIST_PATH = '/system/team/order/list';
const TEAM_DAILY_ORDERS_PATH = '/system/team/order/team_daily_orders';
const OP_DAILY_ORDERS_PATH = '/system/team/op/user_daily_orders';
const TEAM_ORDER_DETAIL_PATH = '/system/team/order/detail';
const TEAM_CALENDAR_DAILY_PATH = '/system/team/order/team_calendar_daily';
const OP_CALENDAR_DAILY_PATH = '/system/team/op/user_calendar_daily';

export interface FetchOrderListPageParams {
  page: number;
  pageSize: number;
  /** 与后端约定一致时传入；`all` 不传 */
  status?: string;
  order_type?: string;
  cleaning_type?: string;
  start_date?: string;
  end_date?: string;
  /** 团队管理员走 `/system/team/order/list`，否则走 `/system/team/op/myorders` */
  asTeamAdmin?: boolean;
}

interface OrderListHttpResponse {
  error?: boolean;
  message?: string;
  data?: {
    orders?: unknown[];
    total?: number;
    pagination?: {
      page?: number;
      page_size?: number;
      total?: number;
      total_pages?: number;
    };
    statistics?: {
      total?: number;
      pending?: number;
      in_progress?: number;
      completed?: number;
      canceled?: number;
    };
  };
}

export interface OrderListStatistics {
  total: number;
  pending: number;
  inProgress: number;
  /** 服务完成待结算 (status=2) */
  serviceDone: number;
  /** 已完结 (status=3/6) */
  completed: number;
  canceled: number;
}

function readString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v);
  }
  return undefined;
}

function pickOrdersArray(data: Record<string, unknown>): unknown[] {
  for (const k of ['orders', 'list', 'records', 'rows']) {
    const v = data[k];
    if (Array.isArray(v)) return v;
  }
  return [];
}

/**
 * 后端 TeamOrder.status：
 * 100/0 pending，1 in_progress，2 service_done，3/6 completed，4 cancelled
 */
export function mapApiStatusToUi(
  raw: number | string | undefined | null,
): Order['status'] {
  const n = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
  if (!Number.isFinite(n)) return 'pending';
  if (n === 4) return 'cancelled';
  if (n === 3 || n === 6) return 'completed';
  if (n === 2) return 'service_done';
  if (n === 1) return 'in_progress';
  if (n === 0 || n === 100) return 'pending';
  return 'pending';
}

/** 分配记录 state：2 已派单，3 服务完成。仅在缺少订单 status 时作后备。 */
function mapAssignmentStateToUi(
  raw: number | string | undefined | null,
): Order['status'] | undefined {
  const n = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
  if (n === 3) return 'service_done';
  if (n === 2) return 'in_progress';
  return undefined;
}

function readAssignDate(o: Record<string, unknown>): string | undefined {
  const assignmentInfo = o.assignment_info ?? o.assignmentInfo;
  if (!assignmentInfo || typeof assignmentInfo !== 'object') return undefined;
  return readString(assignmentInfo as Record<string, unknown>, ['assign_date', 'assignDate']);
}

function progressForUiStatus(
  status: Order['status'],
  createTime?: string,
  assignDate?: string,
): ProgressNode[] {
  const createdDisplay = createTime ? formatServiceTime(createTime) : '--';
  const assignedDisplay = assignDate ? formatServiceTime(assignDate) : '--';
  const nodeTime = (node: ProgressNode) => {
    if (node.id === 'p1' && createdDisplay !== '--') return createdDisplay;
    if (node.id === 'p3' && assignedDisplay !== '--') return assignedDisplay;
    return undefined;
  };

  const base: ProgressNode[] = [
    { id: 'p1', title: 'Order Created', time: createdDisplay, completed: true },
    { id: 'p2', title: 'Pending', time: '--', completed: false },
    { id: 'p3', title: 'In Progress', time: assignedDisplay, completed: false },
    { id: 'p4', title: 'Service Done', time: '--', completed: false },
    { id: 'p5', title: 'Completed', time: '--', completed: false },
  ];

  const withCompletedThrough = (throughIndex: number): ProgressNode[] =>
    base.map((n, i) => ({
      ...n,
      completed: i <= throughIndex,
      time: nodeTime(n) ?? (i <= throughIndex ? '—' : '--'),
    }));

  if (status === 'completed') {
    return withCompletedThrough(4);
  }
  if (status === 'service_done') {
    return withCompletedThrough(3);
  }
  if (status === 'in_progress') {
    return withCompletedThrough(2);
  }
  /* pending / cancelled */
  return withCompletedThrough(1);
}

/** 本地更新订单状态并重建 Progress 时间线 */
export function patchOrderAfterStatusChange(
  order: Order,
  status: Order['status'],
  rawStatus: number,
): Order {
  return {
    ...order,
    status,
    rawStatus,
    progress: progressForUiStatus(status, order.create_time, order.assignDate),
  };
}

/** 标记服务完成后的本地订单更新（含 Progress）。尚未全部完成时保持进行中。 */
export function patchOrderAfterServiceCompleted(
  order: Order,
  allCompleted?: boolean,
): Order {
  if (allCompleted === false) return order;
  return patchOrderAfterStatusChange(order, 'service_done', 2);
}

function formatNowTimestamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** 分配订单后的本地订单更新（含 Progress） */
export function patchOrderAfterAssignment(
  order: Order,
  assigneeIds: string[],
  assignDate?: string,
  servicers?: OrderServicerInfo[],
): Order {
  return patchOrderAfterStatusChange(
    {
      ...order,
      assignees: assigneeIds,
      servicers: servicers ?? order.servicers,
      assignDate: assignDate ?? formatNowTimestamp(),
    },
    'in_progress',
    1,
  );
}

function sameOperatorSet(current: Array<number | string>, next: string[]): boolean {
  if (current.length !== next.length) return false;
  const ids = new Set(current.map(String));
  return next.every((id) => ids.has(String(id)));
}

/** 订单操作保存后的本地更新。未改人员时只更新工时和价格。 */
export function patchOrderAfterOperation(
  order: Order,
  operatorIds: string[] | undefined,
  billing: {
    serviceDuration: number;
    totalPrice: number;
    totalPriceWithGst: number;
    gstRateDecimal: number;
  },
  staff: OrderServicerInfo[],
): Order {
  const billed = patchOrderAfterBilling(order, billing);
  if (!operatorIds) return billed;
  const withStaff: Order = {
    ...billed,
    assignees: operatorIds.map(String),
    servicers: staff,
  };
  if (sameOperatorSet(order.assignees, operatorIds)) return withStaff;
  return patchOrderAfterAssignment(withStaff, operatorIds, undefined, staff);
}

export function patchOrderAfterBilling(
  order: Order,
  billing: {
    serviceDuration: number;
    totalPrice: number;
    totalPriceWithGst: number;
    gstRateDecimal: number;
  },
): Order {
  return {
    ...order,
    serviceDuration: billing.serviceDuration,
    totalPrice: billing.totalPrice,
    totalPriceWithGst: billing.totalPriceWithGst,
    gstRate: Math.round(billing.gstRateDecimal * 1000) / 10,
  };
}

function normalizeGstPercent(raw: unknown): number | undefined {
  if (raw === null || raw === undefined || raw === '') return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return undefined;
  if (n > 0 && n <= 1) return roundGstPercent(n * 100);
  return roundGstPercent(n);
}

function roundGstPercent(value: number): number {
  return Math.round(value * 10) / 10;
}

function normalizeServicerInfo(raw: unknown): OrderServicerInfo[] {
  if (!Array.isArray(raw)) return [];
  const servicers: OrderServicerInfo[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const operatorId = readString(o, ['operator_id', 'operatorId', 'id']);
    if (!operatorId) continue;
    const name = readString(o, ['name']) ?? 'Service Provider';
    servicers.push({
      operatorId,
      name,
      email: readString(o, ['email']),
      mobile: readString(o, ['mobile', 'phone']),
      avatar: readString(o, ['avatar']),
      hourlyWage: readNum(o, ['hourly_wage', 'hourlyWage']),
    });
  }
  return servicers;
}

function normalizeOrderCleanItems(raw: unknown): OrderCleanItem[] {
  if (!Array.isArray(raw)) return [];
  const items: OrderCleanItem[] = [];
  raw.forEach((row, index) => {
    if (!row || typeof row !== 'object') return;
    const o = row as Record<string, unknown>;
    const itemName = readString(o, ['item_name', 'itemName', 'name']);
    if (!itemName) return;
    items.push({
      id: readString(o, ['id', 'clean_item_id', 'cleanItemId']) ?? `clean-item-${index}`,
      itemName,
      quantity: readNum(o, ['quantity', 'qty']) ?? 1,
      duration: readNum(o, ['duration']),
      unitPrice: readNum(o, ['unit_price', 'unitPrice', 'price']),
    });
  });
  return items;
}

/** 单条清洁项目时长小计（duration × quantity） */
export function calcCleanItemDurationSubtotal(item: OrderCleanItem): number {
  const qty = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
  const duration = typeof item.duration === 'number' && item.duration > 0 ? item.duration : 0;
  return duration * qty;
}

/** 汇总清洁项目总时长 */
export function sumCleanItemsDuration(items: OrderCleanItem[] | undefined): number {
  if (!items?.length) return 0;
  return items.reduce((sum, item) => sum + calcCleanItemDurationSubtotal(item), 0);
}

/** 订单总时长：优先 service_duration，否则由清洁项目自动汇总 */
export function resolveOrderServiceDuration(
  serviceDuration: number | undefined,
  cleanItems: OrderCleanItem[] | undefined,
): number | undefined {
  if (typeof serviceDuration === 'number' && serviceDuration > 0) return serviceDuration;
  const computed = sumCleanItemsDuration(cleanItems);
  return computed > 0 ? computed : undefined;
}

function formatHoursLabel(hours: number): string {
  return Number.isInteger(hours) ? `${hours} hours` : `${hours.toFixed(1)} hours`;
}

function formatDurationAmount(hours: number | null | undefined): string | null {
  if (typeof hours !== 'number' || !Number.isFinite(hours) || hours < 0) return null;
  const rounded = Math.round(hours * 100) / 100;
  return Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

/** 总时长展示：`2 H`，不带个人工时标记。 */
export function formatDurationHours(hours: number | null | undefined): string {
  const text = formatDurationAmount(hours);
  return text === null ? '—' : `${text} H`;
}

/** 个人工时展示：`2 H (MTS)`，最多保留两位小数。 */
export function formatDurationMts(hours: number | null | undefined): string {
  const text = formatDurationAmount(hours);
  return text === null ? '—' : `${text} H (MTS)`;
}

/** 清洁项目工作时长：duration × quantity，单位小时 */
export function formatCleanItemWorkHours(
  duration: number | undefined,
  quantity: number | undefined,
): string {
  const total = calcCleanItemDurationSubtotal({
    id: '',
    itemName: '',
    quantity,
    duration,
  });
  if (total <= 0) return '—';
  return formatHoursLabel(total);
}

export function formatOrderDurationHours(hours: number | undefined): string {
  if (typeof hours !== 'number' || hours <= 0) return '—';
  return formatHoursLabel(hours);
}

export function isOrderAssignedToCurrentUser(
  order: Pick<Order, 'assignees' | 'servicers'>,
  currentUserId: string | number | null | undefined,
): boolean {
  if (currentUserId === null || currentUserId === undefined || String(currentUserId).trim() === '') {
    return false;
  }
  const id = String(currentUserId);
  if (order.assignees.some((assigneeId) => String(assigneeId) === id)) return true;
  return (order.servicers ?? []).some((servicer) => String(servicer.operatorId) === id);
}

/** 已分配到当前账号且进行中的订单可标记完成服务 */
export function canUserMarkServiceCompleted(
  order: Pick<Order, 'assignees' | 'servicers' | 'status'>,
  currentUserId: string | number | null | undefined,
): boolean {
  if (order.status !== 'in_progress') return false;
  return isOrderAssignedToCurrentUser(order, currentUserId);
}

export function orderHasAssignedStaff(
  order: Pick<Order, 'assignees' | 'servicers'>,
): boolean {
  if (order.assignees.some((id) => String(id).trim() !== '')) return true;
  return (order.servicers ?? []).some((person) => String(person.operatorId).trim() !== '');
}

/** 清洁人员不能改工时。改工时、加钟只走 canTeamAdminEditWorkHours。 */
export function canUserEditOrderServiceDuration(
  _order: Pick<Order, 'assignees' | 'servicers' | 'status'>,
  _currentUserId: string | number | null | undefined,
): boolean {
  return false;
}

/**
 * 订单完成前，团队管理员可修改总工时（途中加钟）。
 * 仅在服务尚未标记完成时开放：pending / in_progress。
 */
export function canTeamAdminEditWorkHours(
  order: Pick<Order, 'status'>,
  isTeamAdmin: boolean,
): boolean {
  if (!isTeamAdmin) return false;
  return order.status === 'pending' || order.status === 'in_progress';
}

/**
 * 团队管理员在订单完成前可修改预约时间、服务时长与价格
 *（后端 `update_order_before_complete`，status: 100/0/1/2）
 */
export function canTeamAdminEditOrderBeforeComplete(
  order: Pick<Order, 'status' | 'rawStatus'>,
  isTeamAdmin: boolean,
): boolean {
  if (!isTeamAdmin) return false;
  if (order.status === 'cancelled' || order.status === 'completed') {
    return false;
  }

  const raw = order.rawStatus;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw === 100 || raw === 0 || raw === 1 || raw === 2;
  }

  return order.status === 'pending' || order.status === 'in_progress' || order.status === 'service_done';
}

/** @deprecated 使用 canTeamAdminEditOrderBeforeComplete */
export const canTeamAdminEditOrderTime = canTeamAdminEditOrderBeforeComplete;

/** @deprecated 使用 canTeamAdminEditOrderBeforeComplete */
export const canTeamAdminEditOrderPrice = canTeamAdminEditOrderBeforeComplete;

export function formatOrderTotalPrice(price: number | undefined): string {
  if (typeof price !== 'number' || price < 0) return '—';
  const rounded = Math.round(price * 100) / 100;
  const label = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  return `$${label}`;
}

export function resolveOrderTotalPrice(
  totalPrice: number | undefined,
  cleanItems: OrderCleanItem[] | undefined,
): number | undefined {
  if (typeof totalPrice === 'number') return totalPrice;
  if (!cleanItems?.length) return undefined;
  const computed = cleanItems.reduce(
    (sum, item) => sum + (item.unitPrice ?? 0) * (item.quantity ?? 1),
    0,
  );
  return computed > 0 ? computed : undefined;
}

/** 订单列表卡片用的清洁项目简要文案 */
export function formatOrderCleanItemsBrief(
  items: OrderCleanItem[] | undefined,
  maxItems = 3,
): string {
  if (!items?.length) return '';
  const parts = items.slice(0, maxItems).map((item) => {
    if (typeof item.quantity === 'number' && item.quantity > 1) {
      return `${item.itemName} ×${item.quantity}`;
    }
    return item.itemName;
  });
  const rest = items.length - maxItems;
  if (rest > 0) parts.push(`+${rest} more`);
  return parts.join(' · ');
}

function normalizeOrderRow(row: unknown, index: number): Order | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const id =
    readString(o, ['id', 'order_id', 'orderId']) ?? `order-${index}`;
  const custName = readString(o, ['cust_name', 'customer_name', 'name', 'custName']) ?? '—';
  const email = readString(o, ['email', 'cust_email']) ?? '—';
  const phone = readString(o, ['mobile', 'phone', 'cust_phone', 'cust_mobile']) ?? '—';
  const address =
    readString(o, ['address', 'service_address', 'full_address', 'cust_address']) ?? '—';
  const avatar =
    readString(o, ['avatar', 'cust_avatar', 'customer_avatar']) ??
    `https://ui-avatars.com/api/?name=${encodeURIComponent(custName)}&background=e8f5e9&color=1b5e20`;

  const rawOrderStatus = readNum(o, ['status', 'order_status', 'orderStatus']);
  const assignmentInfo = (o.assignment_info ?? o.assignmentInfo ?? {}) as Record<string, unknown>;
  const assignmentState =
    readNum(o, ['service_status', 'serviceStatus'])
    ?? readNum(assignmentInfo, ['assignment_state', 'assignmentState', 'state']);
  const status =
    rawOrderStatus !== undefined
      ? mapApiStatusToUi(rawOrderStatus)
      : (mapAssignmentStateToUi(assignmentState) ?? 'pending');
  const rawStatus = rawOrderStatus ?? assignmentState;

  const serviceType =
    readString(o, [
      'service_type_name',
      'service_type',
      'cleaning_type_name',
      'order_type_name',
    ]) ??
    (() => {
      const ot = readNum(o, ['order_type']);
      if (ot === 0) return 'By project';
      if (ot === 1) return 'By hour';
      const ct = o.cleaning_type;
      return ct !== undefined && ct !== null ? String(ct) : 'Service';
    })();

  const scheduledRaw =
    readString(o, ['service_time', 'scheduled_at', 'serviceTime', 'appointment_time']) ?? '';
  const service_time = scheduledRaw || '—';

  const assigneesRaw = o.assignees ?? o.assignee_ids ?? o.servicer_ids ?? o.assigned_ids;
  let assignees: string[] = [];
  if (Array.isArray(assigneesRaw)) {
    assignees = assigneesRaw.map((x) => String(x)).filter(Boolean);
  } else if (typeof assigneesRaw === 'string' && assigneesRaw.trim()) {
    assignees = assigneesRaw
      .split(/[,;|\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
  }

  const subRemain = readNum(o, ['subscription_remaining', 'subscriptionRemaining']);
  const isSub =
    readNum(o, ['is_subscription', 'isSubscription']) === 1 ||
    o.is_subscription === true ||
    (typeof subRemain === 'number' && subRemain > 0);

  const teamName = readString(o, ['team_name', 'teamName']);
  const teamId = readString(o, ['team_id', 'teamId']);
  const notes = readString(o, ['other_info', 'otherInfo', 'notes', 'remark', 'remarks', 'order_notes']);
  const city = readString(o, ['city']);
  const state = readString(o, ['state']);
  const street = readString(o, ['street']);
  const streetNumber = readString(o, ['street_number', 'streetNumber']);
  const postalCode = readString(o, ['postal_code', 'postalCode']);
  const areaParts = [streetNumber, street].filter((part) => part?.trim());
  const area = areaParts.length > 0 ? areaParts.join(' ') : undefined;
  const create_time = readString(o, ['create_time', 'createTime', 'created_at', 'createdAt']);
  const assign_date = readAssignDate(o);
  const totalPrice = readNum(o, ['total_price', 'totalPrice']);
  const totalPriceWithGst = readNum(o, ['total_price_with_gst', 'totalPriceWithGst']);
  const serviceDuration = readNum(o, ['service_duration', 'serviceDuration']);
  const gstRate = normalizeGstPercent(o.gst_rate ?? o.gstRate);
  const servicers = normalizeServicerInfo(o.servicer_info ?? o.servicerInfo);
  if (assignees.length === 0 && servicers.length > 0) {
    assignees = servicers.map((s) => s.operatorId);
  }
  const cleanItems = normalizeOrderCleanItems(
    o.clean_items ?? o.cleanItems ?? o.details,
  );
  const payStatus = readOrderPayStatus(o);
  const invoiceSent = readOrderInvoiceSent(o);

  return {
    id,
    teamId,
    teamName,
    customer: { name: custName, email, phone, address, avatar },
    serviceType,
    status,
    rawStatus: rawStatus,
    totalPrice,
    totalPriceWithGst,
    serviceDuration,
    gstRate,
    servicers,
    assignees,
    service_time,
    create_time,
    assignDate: assign_date,
    progress: progressForUiStatus(status, create_time, assign_date),
    notes,
    city,
    state,
    street,
    streetNumber,
    area,
    postalCode,
    isSubscription: isSub,
    subscriptionRemaining: typeof subRemain === 'number' ? subRemain : undefined,
    cleanItems,
    payStatus,
    invoiceSent,
  };
}

function readOrderPayStatus(o: Record<string, unknown>): Order['payStatus'] {
  const raw = o.pay_status ?? o.payStatus ?? o.payment_status ?? o.paymentStatus;
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN;
  if (n === 0 || n === 1 || n === 2) return n;
  return undefined;
}

function readOrderInvoiceSent(o: Record<string, unknown>): boolean | undefined {
  const raw = o.invoice_sent ?? o.invoiceSent ?? o.is_invoiced ?? o.has_invoice;
  if (raw === true || raw === 1 || raw === '1' || raw === 'true') return true;
  if (raw === false || raw === 0 || raw === '0' || raw === 'false') return false;
  return undefined;
}

export type InvoiceCloseAction = 'pay_and_close' | 'pay' | 'close';

/** 服务已完成后，团队管理员可确认收款并关闭订单；已关单但未收款时仍要求发票已发送。 */
export function resolveInvoiceCloseAction(
  order: Pick<Order, 'id' | 'status' | 'payStatus' | 'invoiceSent'>,
): InvoiceCloseAction | null {
  if (order.status === 'cancelled' || order.status === 'pending' || order.status === 'in_progress') {
    return null;
  }
  const paid = order.payStatus === 1;
  if (order.status === 'service_done') return paid ? 'close' : 'pay_and_close';
  const sent = order.invoiceSent === true || hasRememberedInvoiceSent(order.id);
  if (!sent) return null;
  if (order.status === 'completed' && !paid) return 'pay';
  return null;
}

export interface OrderListPageResult {
  orders: Order[];
  total: number;
  totalPages: number;
  statistics?: OrderListStatistics;
}

function readPagination(payload: Record<string, unknown>): {
  total: number;
  totalPages: number;
} {
  const pagination = payload.pagination;
  if (pagination && typeof pagination === 'object') {
    const p = pagination as Record<string, unknown>;
    const total = readNum(p, ['total']) ?? 0;
    const totalPages = readNum(p, ['total_pages', 'totalPages']) ?? 0;
    if (total >= 0) {
      return {
        total,
        totalPages: totalPages > 0 ? totalPages : total > 0 ? 1 : 0,
      };
    }
  }

  const total = readNum(payload, ['total']) ?? 0;
  return {
    total,
    totalPages: total > 0 ? Math.ceil(total / 10) : 0,
  };
}

function readStatistics(payload: Record<string, unknown>): OrderListStatistics | undefined {
  const statistics = payload.statistics;
  if (!statistics || typeof statistics !== 'object') return undefined;
  const s = statistics as Record<string, unknown>;
  return {
    total: readNum(s, ['total']) ?? 0,
    pending: readNum(s, ['pending']) ?? 0,
    inProgress: readNum(s, ['in_progress', 'inProgress']) ?? 0,
    serviceDone:
      readNum(s, ['service_done', 'serviceDone', 'awaiting_settlement', 'awaitingSettlement'])
      ?? readNum(s, ['completed'])
      ?? 0,
    completed: readNum(s, ['settled']) ?? 0,
    canceled: readNum(s, ['canceled', 'cancelled']) ?? 0,
  };
}

/** 根据列表接口 statistics 解析各状态 Tab 徽标数量 */
export function resolveOrderTabCount(
  tabKey: string,
  statistics: OrderListStatistics | null | undefined,
  activeTabKey: string,
  activeTabTotal: number,
): number {
  if (tabKey === activeTabKey) return activeTabTotal;
  if (!statistics) return 0;
  switch (tabKey) {
    case 'all':
      return statistics.total;
    case 'pending':
      return statistics.pending;
    case 'in_progress':
      return statistics.inProgress;
    case 'completed':
      return statistics.completed;
    case 'service_done':
      return statistics.serviceDone;
    default:
      return 0;
  }
}

/**
 * POST 订单列表
 * - 团队管理员：`/system/team/order/list`
 * - 其他成员：`/system/team/op/myorders`
 * 参数：status、order_type、cleaning_type、start_date、end_date、page、page_size
 * - status: 0 pending / 1 in_progress / 2 service_done / 3 completed / 4 cancelled
 */
export async function fetchOrderListPage(
  params: FetchOrderListPageParams,
): Promise<OrderListPageResult> {
  const body: Record<string, string | number> = {
    page: params.page,
    page_size: params.pageSize,
  };
  if (params.status !== undefined && params.status !== '') body.status = params.status;
  if (params.order_type !== undefined && params.order_type !== '') body.order_type = params.order_type;
  if (params.cleaning_type?.trim()) body.cleaning_type = params.cleaning_type.trim();
  if (params.start_date?.trim()) body.start_date = params.start_date.trim();
  if (params.end_date?.trim()) body.end_date = params.end_date.trim();

  const path = params.asTeamAdmin ? TEAM_ORDER_LIST_PATH : OP_ORDER_LIST_PATH;
  const raw = await http.post<OrderListHttpResponse>(path, body);
  return parseOrderListResponse(raw, params.pageSize);
}

export interface FetchDailyOrdersParams {
  /** 查询日期 YYYY-MM-DD */
  date: string;
  /** 团队管理员走 `/system/team/order/team_daily_orders`，否则走 `/system/team/op/user_daily_orders` */
  asTeamAdmin?: boolean;
  /** service_time=服务日（默认），create_time=下单日 */
  groupByField?: 'service_time' | 'create_time';
  status?: string;
}

/**
 * POST 日历单日订单（不分页）
 * - 团队管理员：`/system/team/order/team_daily_orders`
 * - 其他成员：`/system/team/op/user_daily_orders`
 */
export async function fetchDailyOrders(
  params: FetchDailyOrdersParams,
): Promise<OrderListPageResult> {
  const date = params.date.trim();
  const body: Record<string, string> = { date };
  if (params.groupByField) body.group_by_field = params.groupByField;
  if (params.status !== undefined && params.status !== '') body.status = params.status;

  const path = params.asTeamAdmin ? TEAM_DAILY_ORDERS_PATH : OP_DAILY_ORDERS_PATH;
  const raw = await http.post<OrderListHttpResponse>(path, body);
  return parseOrderListResponse(raw, 50);
}

function parseOrderListResponse(
  raw: OrderListHttpResponse,
  pageSize: number,
): OrderListPageResult {
  const data = raw.data;
  const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  const rows = pickOrdersArray(payload);
  const orders: Order[] = [];
  rows.forEach((row, i) => {
    const one = normalizeOrderRow(row, i);
    if (one) orders.push(one);
  });

  const { total, totalPages } = readPagination(payload);
  const resolvedTotal = total >= 0 ? total : orders.length;
  const resolvedTotalPages =
    totalPages > 0
      ? totalPages
      : resolvedTotal > 0
        ? Math.ceil(resolvedTotal / pageSize)
        : 0;

  return {
    orders,
    total: resolvedTotal,
    totalPages: resolvedTotalPages,
    statistics: readStatistics(payload),
  };
}

function unwrapOrderDetailPayload(data: unknown): unknown {
  if (!data || typeof data !== 'object') return data;
  if (Array.isArray(data)) return data[0];
  const o = data as Record<string, unknown>;
  if (o.order && typeof o.order === 'object') return o.order;
  const rows = pickOrdersArray(o);
  if (rows.length > 0) return rows[0];
  return data;
}

/** POST `/system/team/order/detail` — 团队订单详情 */
export async function fetchOrderDetail(orderId: number | string): Promise<Order> {
  const formData = new FormData();
  formData.append('order_id', String(orderId));

  const raw = await http.post<OrderListHttpResponse>(TEAM_ORDER_DETAIL_PATH, formData, {
    skipApiEnvelopeModal: true,
  });
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to load order detail.');
  }

  const one = normalizeOrderRow(unwrapOrderDetailPayload(raw.data), 0);
  if (!one) {
    throw new ApiRequestError('Failed to load order detail.');
  }
  return one;
}

/** 日历单日已结单金额：不含税 / 含税。 */
export interface CalendarCompletedAmounts {
  amountCompleted: number;
  amountCompletedWithGst: number;
}

/** POST `/system/team/op/user_calendar_daily` — 工作人员当日日程 + 状态数量 / 月档期标记 */
export interface CalendarDailyResult {
  orders: Order[];
  /** Selected day's status counts for badge strip */
  statusCounts: CalendarDayStatusCounts;
  /** YYYY-MM-DD → status counts for calendar dots */
  days: Record<string, CalendarDayStatusCounts>;
  /** YYYY-MM-DD → 已结单金额。团队日历接口才有。 */
  dayAmounts: Record<string, CalendarCompletedAmounts>;
  /** `month_summary` 的整月已结单金额。 */
  monthAmounts: CalendarCompletedAmounts | null;
}

function readStatusCounts(raw: unknown): CalendarDayStatusCounts {
  const counts = createEmptyCalendarStatusCounts();
  if (!raw || typeof raw !== 'object') return counts;
  const o = raw as Record<string, unknown>;

  const pending = readNum(o, ['pending', 'Pending']);
  const inProgress = readNum(o, ['in_progress', 'inProgress']);
  const completed = readNum(o, ['completed', 'Completed']);
  const serviceDone = readNum(o, ['service_done', 'serviceDone']);
  const cancelled = readNum(o, ['cancelled', 'canceled', 'Cancelled']);
  const hasNamed =
    pending != null
    || inProgress != null
    || completed != null
    || serviceDone != null
    || cancelled != null;

  if (hasNamed) {
    counts.pending = pending ?? 0;
    counts.in_progress = inProgress ?? 0;
    counts.completed = completed ?? 0;
    counts.service_done = serviceDone ?? 0;
    counts.cancelled = cancelled ?? 0;
    return counts;
  }

  const status0 = readNum(o, ['0']) ?? 0;
  const status100 = readNum(o, ['100']) ?? 0;
  counts.pending = status0 + status100;
  counts.in_progress = readNum(o, ['1']) ?? 0;
  counts.service_done = readNum(o, ['2']) ?? 0;
  counts.completed = (readNum(o, ['3']) ?? 0) + (readNum(o, ['6']) ?? 0);
  counts.cancelled = readNum(o, ['4']) ?? 0;
  return counts;
}

function countsFromOrders(orders: Order[]): CalendarDayStatusCounts {
  const counts = createEmptyCalendarStatusCounts();
  for (const order of orders) {
    switch (order.status) {
      case 'pending':
        counts.pending += 1;
        break;
      case 'in_progress':
        counts.in_progress += 1;
        break;
      case 'service_done':
        counts.service_done += 1;
        break;
      case 'completed':
        counts.completed += 1;
        break;
      case 'cancelled':
        counts.cancelled += 1;
        break;
      default:
        break;
    }
  }
  return counts;
}

function readDaySummary(row: unknown): CalendarDayStatusCounts {
  if (!row || typeof row !== 'object') return createEmptyCalendarStatusCounts();
  const o = row as Record<string, unknown>;
  return readStatusCounts(
    o.summary ?? o.status_counts ?? o.statusCounts ?? o.counts ?? o.statistics ?? o,
  );
}

function pickDayStatusMap(data: Record<string, unknown>): Record<string, CalendarDayStatusCounts> {
  const map: Record<string, CalendarDayStatusCounts> = {};

  const dailyRows = [data.daily, data.days, data.calendar, data.list].find(Array.isArray) as
    | unknown[]
    | undefined;
  if (dailyRows) {
    for (const row of dailyRows) {
      if (!row || typeof row !== 'object') continue;
      const o = row as Record<string, unknown>;
      const iso = readString(o, ['date', 'day', 'service_date', 'serviceDate']);
      if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
      map[iso] = readDaySummary(o);
    }
    if (Object.keys(map).length > 0) return map;
  }

  const candidates = [
    data.days,
    data.calendar,
    data.date_status,
    data.dateStatus,
    data.month_days,
    data.monthDays,
    data.day_stats,
    data.dayStats,
    data.status_by_date,
    data.statusByDate,
  ];

  for (const candidate of candidates) {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) continue;
    for (const [dateKey, value] of Object.entries(candidate as Record<string, unknown>)) {
      const iso = dateKey.trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
      map[iso] = readDaySummary(value);
    }
    if (Object.keys(map).length > 0) return map;
  }

  return map;
}

function readCompletedAmounts(raw: unknown): CalendarCompletedAmounts | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const amountCompleted = readNum(o, ['amount_completed', 'amountCompleted']);
  const amountCompletedWithGst = readNum(o, ['amount_completed_with_gst', 'amountCompletedWithGst']);
  if (amountCompleted == null && amountCompletedWithGst == null) return null;
  return {
    amountCompleted: amountCompleted ?? 0,
    amountCompletedWithGst: amountCompletedWithGst ?? 0,
  };
}

function pickDayAmounts(data: Record<string, unknown>): Record<string, CalendarCompletedAmounts> {
  const map: Record<string, CalendarCompletedAmounts> = {};
  const dailyRows = [data.daily, data.days, data.calendar, data.list].find(Array.isArray) as
    | unknown[]
    | undefined;
  if (!dailyRows) return map;
  for (const row of dailyRows) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const iso = readString(o, ['date', 'day', 'service_date', 'serviceDate']);
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
    const amounts = readCompletedAmounts(o);
    if (amounts) map[iso] = amounts;
  }
  return map;
}

function pickMonthAmounts(data: Record<string, unknown>): CalendarCompletedAmounts | null {
  const summary = data.month_summary ?? data.monthSummary;
  return readCompletedAmounts(summary);
}

export async function fetchCalendarDaily(
  date: string,
  month?: string,
  asTeamCreator = false,
): Promise<CalendarDailyResult> {
  const trimmedDate = date.trim();
  const body: Record<string, string | number> = { date: trimmedDate, week_start: 0 };
  const monthKey = (month ?? trimmedDate.slice(0, 7)).trim();
  if (/^\d{4}-\d{2}$/.test(monthKey)) {
    body.month = monthKey;
  }

  const path = asTeamCreator ? TEAM_CALENDAR_DAILY_PATH : OP_CALENDAR_DAILY_PATH;
  const raw = await http.post<OrderListHttpResponse>(path, body);
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to load calendar.');
  }

  const data = raw.data;
  const rows = Array.isArray(data)
    ? data
    : data && typeof data === 'object'
      ? pickOrdersArray(data as Record<string, unknown>)
      : [];
  const orders: Order[] = [];
  rows.forEach((row, i) => {
    const one = normalizeOrderRow(row, i);
    if (one) orders.push(one);
  });

  const derived = countsFromOrders(orders);
  const days: Record<string, CalendarDayStatusCounts> = {};
  let dayAmounts: Record<string, CalendarCompletedAmounts> = {};
  let monthAmounts: CalendarCompletedAmounts | null = null;

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const payload = data as Record<string, unknown>;
    Object.assign(days, pickDayStatusMap(payload));
    dayAmounts = pickDayAmounts(payload);
    monthAmounts = pickMonthAmounts(payload);
  }

  const fromDaily = days[trimmedDate];
  const statusCounts = hasCalendarStatusCounts(fromDaily)
    ? fromDaily
    : hasCalendarStatusCounts(derived)
      ? derived
      : createEmptyCalendarStatusCounts();

  return { orders, statusCounts, days, dayAmounts, monthAmounts };
}

/** 状态 Tab 与 `/system/team/op/myorders` 的 `status` 查询参数。 */
export function uiTabToListStatus(tab: string): string | undefined {
  switch (tab) {
    case 'pending':
      return '0';
    case 'in_progress':
      return '1';
    case 'service_done':
      return '2';
    case 'completed':
      return '3';
    case 'cancelled':
      return '4';
    case 'all':
    default:
      return undefined;
  }
}

/** 将日期选择器的 YYYY-MM-DD 转为后端要求的 datetime 字符串。 */
export function toFilterStartDate(date: string): string {
  return `${date.trim()} 00:00:00`;
}

export function toFilterEndDate(date: string): string {
  return `${date.trim()} 23:59:59`;
}

export interface TeamOrderPeriodStats {
  newOrders: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  paidAmount: number;
  unpaidAmount: number;
}

export type TeamOrderStatsPeriodKey = 'today' | 'week' | 'month';

export type TeamOrderStats = Record<TeamOrderStatsPeriodKey, TeamOrderPeriodStats>;

interface TeamOrderStatsHttpResponse {
  error?: boolean;
  message?: string;
  data?: Record<string, unknown>;
}

function normalizePeriodStats(raw: unknown): TeamOrderPeriodStats {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    newOrders: readNum(o, ['new', 'newOrders']) ?? 0,
    inProgress: readNum(o, ['in_progress', 'inProgress']) ?? 0,
    completed: readNum(o, ['completed']) ?? 0,
    cancelled: readNum(o, ['cancelled', 'canceled']) ?? 0,
    paidAmount: readNum(o, ['paid_amount', 'paidAmount']) ?? 0,
    unpaidAmount: readNum(o, ['unpaid_amount', 'unpaidAmount']) ?? 0,
  };
}

const EMPTY_PERIOD_STATS: TeamOrderPeriodStats = {
  newOrders: 0,
  inProgress: 0,
  completed: 0,
  cancelled: 0,
  paidAmount: 0,
  unpaidAmount: 0,
};

/** POST `/system/team/order/get_team_order_stats` — 团队管理员订单统计（today / week / month）。 */
export async function fetchTeamOrderStats(): Promise<TeamOrderStats> {
  const raw = await http.post<TeamOrderStatsHttpResponse>('/system/team/order/get_team_order_stats');
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to load team stats.');
  }
  const data = raw.data;
  const payload = data && typeof data === 'object' ? data : {};
  return {
    today: normalizePeriodStats(payload.today),
    week: normalizePeriodStats(payload.week),
    month: normalizePeriodStats(payload.month),
  };
}

export function teamStatsPeriodKey(period: 'Today' | 'This Week' | 'This Month'): TeamOrderStatsPeriodKey {
  switch (period) {
    case 'This Week':
      return 'week';
    case 'This Month':
      return 'month';
    case 'Today':
    default:
      return 'today';
  }
}

export const EMPTY_TEAM_ORDER_STATS: TeamOrderStats = {
  today: { ...EMPTY_PERIOD_STATS },
  week: { ...EMPTY_PERIOD_STATS },
  month: { ...EMPTY_PERIOD_STATS },
};

export interface ServiceTypeDistribution {
  standard: number;
  office: number;
  airbnb: number;
  others: number;
}

export type TeamServiceTypeStats = Record<TeamOrderStatsPeriodKey, ServiceTypeDistribution>;

export interface ServiceDistributionItem {
  key: string;
  type: string;
  count: number;
  pct: number;
  color: string;
}

const SERVICE_TYPE_META: { key: keyof ServiceTypeDistribution; label: string; color: string }[] = [
  { key: 'standard', label: 'Standard Cleaning', color: 'bg-green-500' },
  { key: 'office', label: 'Office Cleaning', color: 'bg-teal-400' },
  { key: 'airbnb', label: 'Airbnb Cleaning', color: 'bg-emerald-400' },
  { key: 'others', label: 'Others', color: 'bg-sky-400' },
];

const EMPTY_SERVICE_TYPE_DISTRIBUTION: ServiceTypeDistribution = {
  standard: 0,
  office: 0,
  airbnb: 0,
  others: 0,
};

function normalizeServiceTypeDistribution(raw: unknown): ServiceTypeDistribution {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    standard: readNum(o, ['standard']) ?? 0,
    office: readNum(o, ['office']) ?? 0,
    airbnb: readNum(o, ['airbnb']) ?? 0,
    others: readNum(o, ['others']) ?? 0,
  };
}

/** POST `/system/team/order/get_team_service_type_stats` — 团队清洁类型分布（today / week / month）。 */
export async function fetchTeamServiceTypeStats(): Promise<TeamServiceTypeStats> {
  const raw = await http.post<TeamOrderStatsHttpResponse>('/system/team/order/get_team_service_type_stats');
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to load service distribution.');
  }
  const data = raw.data;
  const payload = data && typeof data === 'object' ? data : {};
  return {
    today: normalizeServiceTypeDistribution(payload.today),
    week: normalizeServiceTypeDistribution(payload.week),
    month: normalizeServiceTypeDistribution(payload.month),
  };
}

export const EMPTY_TEAM_SERVICE_TYPE_STATS: TeamServiceTypeStats = {
  today: { ...EMPTY_SERVICE_TYPE_DISTRIBUTION },
  week: { ...EMPTY_SERVICE_TYPE_DISTRIBUTION },
  month: { ...EMPTY_SERVICE_TYPE_DISTRIBUTION },
};

export function toServiceDistributionItems(distribution: ServiceTypeDistribution): ServiceDistributionItem[] {
  const total = SERVICE_TYPE_META.reduce((sum, item) => sum + distribution[item.key], 0);
  return SERVICE_TYPE_META.map(({ key, label, color }) => {
    const count = distribution[key];
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return { key, type: label, count, pct, color };
  });
}

/** `/system/team/order/servicer/availablelist` 可用服务人员 */
export interface AvailableServicer {
  operatorId: string;
  name: string;
  avatar: string;
  city: string;
  mobile?: string;
  email?: string;
  createDate?: string;
  isAvailable: boolean;
  isHoliday: boolean;
  inRegion: boolean;
  typeText: string;
  /** 时薪，`hourly_wage` */
  hourlyWage?: number;
  /** 在线状态（`online` 字段） */
  isOnline: boolean;
  /** 负责服务区域邮编 */
  postalCodes?: string[];
}

export function hasServicerHourlyWage(servicer: Pick<AvailableServicer, 'hourlyWage'>): boolean {
  return typeof servicer.hourlyWage === 'number'
    && Number.isFinite(servicer.hourlyWage)
    && servicer.hourlyWage > 0;
}

export interface AvailableServicersPageResult {
  items: AvailableServicer[];
  total: number;
  page: number;
  pageSize: number;
}

function pickAvailableServicerRows(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== 'object') return [];
  const o = data as Record<string, unknown>;
  if (Array.isArray(o.list)) return o.list;
  for (const k of ['items', 'records', 'members']) {
    if (Array.isArray(o[k])) return o[k] as unknown[];
  }
  return [];
}

function parseServicerPostalCodes(o: Record<string, unknown>, city: string): string[] {
  const fromList = parsePostalCodeList(
    o.postal_code_lst ?? o.postal_codes ?? o.postalCodes ?? o.service_areas ?? o.serviceAreas,
  );
  if (fromList.length > 0) return fromList;
  const single = readString(o, ['postal_code', 'postalCode']);
  if (single) {
    return single.split(/[,;|\/]+/).map((s) => s.trim()).filter(Boolean);
  }
  if (city) {
    return city.split(/[,;|\/]+/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

function parseAvailableServicerRow(row: unknown, index: number): AvailableServicer | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const operatorId = o.operator_id ?? o.operatorId ?? o.id;
  if (operatorId === undefined || operatorId === null || operatorId === '') return null;

  const name = readString(o, ['name']) ?? '—';
  const online = o.online;
  const isOnline = online === 1 || online === '1' || online === true;
  const state = readNum(o, ['state']);
  const enabled = state === undefined || state === 1;
  const isAvailableRaw = o.is_available ?? o.isAvailable;
  let isAvailable = enabled;
  if (isAvailableRaw === false || isAvailableRaw === 0) {
    isAvailable = false;
  } else if (isAvailableRaw === true || isAvailableRaw === 1) {
    isAvailable = enabled;
  } else if (online !== undefined) {
    isAvailable = enabled && isOnline;
  }
  return {
    operatorId: String(operatorId),
    name,
    avatar:
      readString(o, ['avatar']) ??
      `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=e8f5e9&color=1b5e20`,
    city: readString(o, ['city']) ?? '',
    mobile: readString(o, ['mobile', 'phone']),
    email: readString(o, ['email']),
    createDate: readString(o, ['create_date', 'createDate']),
    isAvailable,
    isHoliday: o.is_holiday === true || o.is_holiday === 1,
    inRegion: o.in_region !== false,
    typeText:
      readString(o, ['type_text', 'typeText']) ??
      (isOnline ? 'Online' : 'Offline'),
    hourlyWage: readNum(o, ['hourly_wage', 'hourlyWage']),
    isOnline,
    postalCodes: parseServicerPostalCodes(o, readString(o, ['city']) ?? ''),
  };
}

interface AvailableServicerHttpResponse {
  error?: boolean;
  message?: string;
  data?: unknown[] | {
    list?: unknown[];
    total?: number;
  };
  total?: number;
}

/** POST `/system/team/order/servicer/availablelist` */
export async function fetchAvailableServicersPage(params?: {
  city?: string;
  serviceTime?: string;
  orderId?: number | string;
  page?: number;
  pageSize?: number;
  /** 服务区域邮编；传入时按当地工作人员筛选，省略则不限制 */
  postalCode?: string;
}): Promise<AvailableServicersPageResult> {
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 200;
  const body: Record<string, string | number> = {
    page,
    pageSize,
  };
  if (params?.city?.trim()) body.city = params.city.trim();
  if (params?.serviceTime?.trim()) body.service_time = params.serviceTime.trim();
  if (params?.orderId !== undefined && params.orderId !== '') body.order_id = params.orderId;
  if (params?.postalCode?.trim()) body.postal_code = params.postalCode.trim();

  const raw = await http.post<AvailableServicerHttpResponse>(
    '/system/team/order/servicer/availablelist',
    body,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to load servicers.');
  }
  const list = pickAvailableServicerRows(raw.data);
  const servicers: AvailableServicer[] = [];
  list.forEach((row, i) => {
    const one = parseAvailableServicerRow(row, i);
    if (one) servicers.push(one);
  });
  const payload = raw.data && typeof raw.data === 'object' && !Array.isArray(raw.data)
    ? (raw.data as Record<string, unknown>)
    : {};
  const total =
    typeof raw.total === 'number' && raw.total >= 0
      ? raw.total
      : typeof payload.total === 'number' && payload.total >= 0
        ? payload.total
        : servicers.length;
  return { items: servicers, total, page, pageSize };
}

/** POST `/system/team/order/servicer/availablelist` — 返回全部可用服务人员（最多 200 条） */
export async function fetchAvailableServicers(params?: {
  city?: string;
  serviceTime?: string;
  orderId?: number | string;
  page?: number;
  pageSize?: number;
  postalCode?: string;
}): Promise<AvailableServicer[]> {
  const { items } = await fetchAvailableServicersPage(params);
  return items;
}

/** 将订单服务时间转为 availablelist 接口所需的 ISO 格式 */
export function toAvailableServicerServiceTime(serviceTime: string): string | undefined {
  const raw = serviceTime.trim();
  if (!raw || raw === '—') return undefined;
  if (raw.includes('T')) return raw;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(raw)) return raw.replace(' ', 'T');
  return undefined;
}

const SERVICE_TYPE_TO_CLEANING: Record<string, string> = {
  Standard: 'standard',
  Office: 'office',
  Airbnb: 'airbnb',
  Others: 'others',
};

/** Convert UI time slot (e.g. `08:00 AM`) to 24h `HH:mm:ss`. */
export function timeSlotToServiceTime(date: string, timeSlot: string): string {
  const match = timeSlot.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return `${date.trim()} 09:00:00`;

  let hour = Number(match[1]);
  const minute = match[2];
  const meridiem = match[3].toUpperCase();
  if (meridiem === 'AM') {
    if (hour === 12) hour = 0;
  } else if (hour !== 12) {
    hour += 12;
  }
  const hh = String(hour).padStart(2, '0');
  return `${date.trim()} ${hh}:${minute}:00`;
}

/** 从订单 `service_time` 解析出日期与时段（用于 Edit Time 弹窗回填） */
export function parseServiceTimeToDateAndSlot(serviceTime: string): {
  date: string;
  timeSlot: string;
} | null {
  const raw = serviceTime.trim();
  if (!raw || raw === '—') return null;

  let date = '';
  let hour24 = 9;
  let minute = '00';

  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})/);
  if (isoMatch) {
    date = isoMatch[1];
    hour24 = Number(isoMatch[2]);
    minute = isoMatch[3];
  } else {
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    const ms = n > 1e12 ? n : n > 1e9 ? n * 1000 : NaN;
    if (!Number.isFinite(ms)) return null;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    date = `${y}-${m}-${day}`;
    hour24 = d.getHours();
    minute = String(d.getMinutes()).padStart(2, '0');
  }

  const meridiem = hour24 >= 12 ? 'PM' : 'AM';
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  // Snap to nearest half-hour display used by TIME_SLOTS (on the hour)
  return {
    date,
    timeSlot: `${String(hour12).padStart(2, '0')}:${minute} ${meridiem}`,
  };
}

/** Backend form datetime: `YYYY-MM-DD HH:mm:ss` */
export function formatServiceTimeForUpdate(serviceTime: string): string {
  const parsed = parseServiceTimeToDateAndSlot(serviceTime);
  if (parsed?.date) {
    return timeSlotToServiceTime(parsed.date, parsed.timeSlot);
  }
  const raw = serviceTime.trim();
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (iso) {
    return `${iso[1]} ${iso[2]}:${iso[3]}:${iso[4] ?? '00'}`;
  }
  return raw.replace('T', ' ').slice(0, 19);
}

/** ISO-like string for servicer availability API (`YYYY-MM-DDTHH:mm:ss`). */
export function toServicerAvailabilityTime(date: string, timeSlot: string): string {
  return timeSlotToServiceTime(date, timeSlot).replace(' ', 'T');
}

export interface CreateOrderBody {
  custName: string;
  email: string;
  phone?: string;
  address: string;
  state: string;
  street: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  /** 清洁类型标签，未传时默认 standard */
  serviceType?: string;
  /** `YYYY-MM-DD HH:mm:ss` */
  serviceTime: string;
  otherInfo?: string;
  /** 不含 GST 总价 */
  totalPrice: number;
  /** 含 GST 总价（提交前会按 gstRateDecimal 与 totalPrice 对齐） */
  totalPriceWithGst: number;
  /** 团队 GST 小数税率，用于提交前对齐含税价 */
  gstRateDecimal: number;
  /** 服务时长（小时） */
  serviceDuration: number;
  orderItems?: Array<{ clean_item_id: number; unit_price: number; quantity: number; duration?: number }>;
  /** 进门方式，默认 `code` */
  access?: string;
  /** 创建时指定服务人员（可选） */
  operatorId?: string | number;
  /** 支付状态：0 待支付，1 已支付，2 已取消 */
  paymentStatus?: 0 | 1 | 2;
  assigneeIds?: string[];
}

/** 汇总创建订单各清洁项目工时（duration × quantity） */
export function sumCreateOrderItemsDuration(
  items: CreateOrderBody['orderItems'],
): number {
  return items.reduce((sum, item) => {
    const qty = item.quantity > 0 ? item.quantity : 0;
    const duration = typeof item.duration === 'number' && item.duration > 0 ? item.duration : 0;
    return sum + duration * qty;
  }, 0);
}

export interface CreateOrderResult {
  orderId: number | string;
  message: string;
}

interface CreateOrderHttpResponse {
  error?: boolean;
  message?: string;
  data?: {
    order_id?: number | string;
    orderId?: number | string;
    id?: number | string;
  };
}

function resolveCreatedOrderId(
  data: CreateOrderHttpResponse['data'],
): number | string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const orderId = data.order_id ?? data.orderId ?? data.id;
  if (orderId === undefined || orderId === null || orderId === '') return undefined;
  return orderId;
}

interface AssignOrderHttpResponse {
  error?: boolean;
  message?: string;
}

export interface AssignOrderBody {
  orderId: number | string;
  operatorIds: string[];
  /** 分配时同步订单总工时。传入后由 `/system/team/order/assign` 写入。 */
  serviceDuration?: number;
}

export interface AssignOrderResult {
  message: string;
}

/** POST `/system/team/order/assign` — 分配订单工作人员 */
export async function postAssignOrder(body: AssignOrderBody): Promise<AssignOrderResult> {
  const raw = await http.post<AssignOrderHttpResponse>(
    '/system/team/order/assign',
    {
      order_id: body.orderId,
      operator_ids: body.operatorIds.join(','),
      ...(typeof body.serviceDuration === 'number' && body.serviceDuration > 0
        ? { service_duration: body.serviceDuration }
        : {}),
    },
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to assign order.');
  }
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Assignment confirmed successfully.',
  };
}

export type OrderPaymentMethod = 'transfer' | 'cash';

export interface AdminCompleteOrderBody {
  orderId: number | string;
  /** 结算时确认/调整后的订单总工时 */
  serviceDuration: number;
  durationAllocations: Array<{
    operator_id: number | string;
    duration: number;
  }>;
  totalPrice?: number;
  totalPriceWithGst?: number;
  gstRateDecimal?: number;
  /** 收款方式：转账 / 现金 */
  paymentMethod?: OrderPaymentMethod;
  /** 到账日期 YYYY-MM-DD */
  paymentDate?: string;
}

export interface AdminCompleteOrderResult {
  message: string;
}

/** POST `/system/team/order/admin_complete_order` — 团队管理员结算订单 */
export async function postAdminCompleteOrder(
  body: AdminCompleteOrderBody,
): Promise<AdminCompleteOrderResult> {
  const formData = new FormData();
  formData.append('order_id', String(body.orderId));
  formData.append('service_duration', String(body.serviceDuration));
  if (typeof body.totalPrice === 'number' && Number.isFinite(body.totalPrice)) {
    formData.append('total_price', String(body.totalPrice));
  }
  if (typeof body.totalPriceWithGst === 'number' && Number.isFinite(body.totalPriceWithGst)) {
    formData.append('total_price_with_gst', String(body.totalPriceWithGst));
  }
  if (typeof body.gstRateDecimal === 'number' && Number.isFinite(body.gstRateDecimal) && body.gstRateDecimal >= 0) {
    formData.append('gst_rate', String(body.gstRateDecimal));
  }
  if (body.paymentMethod) {
    formData.append('payment_method', body.paymentMethod);
  }
  if (body.paymentDate?.trim()) {
    formData.append('payment_date', body.paymentDate.trim());
  }
  formData.append(
    'duration_allocations',
    JSON.stringify(
      body.durationAllocations.map((item) => ({
        operator_id: item.operator_id,
        duration: item.duration,
      })),
    ),
  );

  const raw = await http.post<AssignOrderHttpResponse>(
    '/system/team/order/admin_complete_order',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to settle order.');
  }
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Order settled successfully.',
  };
}

export interface CancelAssignmentBody {
  orderId: number | string;
  cancelReason: string;
}

export interface CancelAssignmentResult {
  message: string;
}

export interface AdminReorderBody {
  orderId: number | string;
  /** 新服务日期 YYYY-MM-DD */
  date: string;
  /** 服务时间 HH:MM 或 HH:MM:SS。不传则沿用原订单钟点 */
  time?: string;
  otherInfo?: string;
}

export interface AdminReorderResult {
  message: string;
  newOrderId?: number | string;
}

/** POST `/system/team/order/admin_reorder` — 团队管理员一键复购 */
export async function postAdminReorder(body: AdminReorderBody): Promise<AdminReorderResult> {
  const formData = new FormData();
  formData.append('order_id', String(body.orderId));
  formData.append('date', body.date.trim());
  if (body.time?.trim()) {
    formData.append('time', body.time.trim());
  }
  if (body.otherInfo?.trim()) {
    formData.append('other_info', body.otherInfo.trim());
  }

  const raw = await http.post<AssignOrderHttpResponse & {
    data?: { new_order_id?: number | string };
  }>(
    '/system/team/order/admin_reorder',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to reorder.');
  }
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Reorder success!',
    newOrderId: raw.data?.new_order_id,
  };
}

/** POST `/system/team/order/cancel_assignment` — 团队管理员取消派单 */
export async function postCancelAssignment(
  body: CancelAssignmentBody,
): Promise<CancelAssignmentResult> {
  const formData = new FormData();
  formData.append('order_id', String(body.orderId));
  formData.append('cancel_reason', body.cancelReason.trim());

  const raw = await http.post<AssignOrderHttpResponse>(
    '/system/team/order/cancel_assignment',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to cancel order.');
  }
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Order assignment cancelled successfully.',
  };
}

export interface MarkServiceCompletedBody {
  orderId: number | string;
}

export interface MarkServiceCompletedResult {
  message: string;
  allCompleted: boolean;
}

export interface UpdateServiceDurationBody {
  orderId: number | string;
  serviceDuration: number;
}

export interface UpdateServiceDurationResult {
  message: string;
}

/**
 * POST 修改订单服务总时长。
 * - `asTeamAdmin: true` → 团队管理员接口 `/system/team/order/update_service_duration`（后端要求团长权限）
 * - `asTeamAdmin: false`（默认）→ 团员自助接口 `/system/team/op/update_service_duration`（仅可修改本人被分配的订单）
 * 调用方须根据 `canTeamAdminEditWorkHours(order, isTeamAdmin)` 的结果传入正确的 `asTeamAdmin`。
 */
export async function postUpdateServiceDuration(
  body: UpdateServiceDurationBody,
  options?: { asTeamAdmin?: boolean },
): Promise<UpdateServiceDurationResult> {
  if (body.serviceDuration <= 0) {
    throw new ApiRequestError('Service duration must be greater than 0.');
  }

  const formData = new FormData();
  formData.append('order_id', String(body.orderId));
  formData.append('service_duration', String(body.serviceDuration));

  const raw = await http.post<AssignOrderHttpResponse>(
    options?.asTeamAdmin
      ? '/system/team/order/update_service_duration'
      : '/system/team/op/update_service_duration',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to update service duration.',
    );
  }
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Service duration updated.',
  };
}

export interface UpdateOrderBeforeCompleteBody {
  orderId: number | string;
  /** 预约服务时间，如 `2026-07-23 09:00:00` */
  serviceTime: string;
  serviceDuration: number;
  /** 不含 GST 总价 */
  totalPrice: number;
  /** 含 GST 总价 */
  totalPriceWithGst: number;
  /** 订单 GST 小数税率（如 0.10） */
  gstRateDecimal?: number;
  /** 选中的服务人员。传入后与原分配不一致时重新分配。 */
  operatorIds?: string[];
}

export interface UpdateOrderBeforeCompleteResult {
  message: string;
}

/** POST `/system/team/order/update_order_before_complete` — 完成前修改时间 / 时长 / 价格 */
export async function postUpdateOrderBeforeComplete(
  body: UpdateOrderBeforeCompleteBody,
): Promise<UpdateOrderBeforeCompleteResult> {
  if (body.serviceDuration <= 0) {
    throw new ApiRequestError('Service duration must be greater than 0.');
  }
  const serviceTime = body.serviceTime.trim();
  if (!serviceTime) {
    throw new ApiRequestError('Service time is required.');
  }
  if (body.totalPrice <= 0 || body.totalPriceWithGst <= 0) {
    throw new ApiRequestError('Order total must be greater than 0.');
  }

  const formData = new FormData();
  formData.append('order_id', String(body.orderId));
  formData.append('service_time', serviceTime);
  formData.append('service_duration', String(body.serviceDuration));
  formData.append('total_price', String(body.totalPrice));
  formData.append('total_price_with_gst', String(body.totalPriceWithGst));
  if (typeof body.gstRateDecimal === 'number' && Number.isFinite(body.gstRateDecimal) && body.gstRateDecimal >= 0) {
    formData.append('gst_rate', String(body.gstRateDecimal));
  }
  if (body.operatorIds && body.operatorIds.length > 0) {
    formData.append('operator_ids', body.operatorIds.join(','));
  }

  const raw = await http.post<AssignOrderHttpResponse>(
    '/system/team/order/update_order_before_complete',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to update order.',
    );
  }
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Order updated.',
  };
}

/** 分配 / 完成服务 / 结算前同步工时、费用、税率和人员。 */
export async function postOrderBillingAdjustment(
  order: Order,
  billing: {
    serviceDuration: number;
    totalPrice: number;
    totalPriceWithGst: number;
    gstRateDecimal: number;
  },
  operatorIds?: string[],
): Promise<UpdateOrderBeforeCompleteResult> {
  return postUpdateOrderBeforeComplete({
    orderId: order.id,
    serviceTime: formatServiceTimeForUpdate(order.service_time),
    serviceDuration: billing.serviceDuration,
    totalPrice: billing.totalPrice,
    totalPriceWithGst: billing.totalPriceWithGst,
    gstRateDecimal: billing.gstRateDecimal,
    operatorIds,
  });
}

export interface PreviewInvoiceResult {
  message: string;
  invoiceBase64: string;
  invoiceType?: 'pdf' | 'image';
  invoiceNumber?: string;
}

export interface SendInvoiceResult {
  message: string;
  invoiceNumber?: string;
}

/** POST `/system/team/order/preview_invoice` — 预览订单发票（返回 base64 PDF） */
export async function postPreviewInvoice(
  orderId: number | string,
): Promise<PreviewInvoiceResult> {
  const formData = new FormData();
  formData.append('order_id', String(orderId));

  const raw = await http.post<AssignOrderHttpResponse & {
    data?: {
      invoice_base64?: string;
      invoiceBase64?: string;
      invoice_type?: string;
      invoiceType?: string;
      invoice_number?: string;
      invoiceNumber?: string;
    };
  }>(
    '/system/team/order/preview_invoice',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to preview invoice.',
    );
  }

  const base64 = raw.data?.invoice_base64 ?? raw.data?.invoiceBase64;
  if (!base64 || typeof base64 !== 'string') {
    throw new ApiRequestError('Invoice preview data is missing.');
  }

  const invoiceTypeRaw = raw.data?.invoice_type ?? raw.data?.invoiceType;
  const invoiceType = invoiceTypeRaw === 'pdf' ? 'pdf' : 'image';

  return {
    message: typeof raw.message === 'string' ? raw.message : 'Invoice preview generated.',
    invoiceBase64: base64,
    invoiceType,
    invoiceNumber: raw.data?.invoice_number ?? raw.data?.invoiceNumber,
  };
}

/** POST `/system/team/order/send_invoice` — 发送发票到客户邮箱 */
export async function postSendInvoice(
  orderId: number | string,
): Promise<SendInvoiceResult> {
  const formData = new FormData();
  formData.append('order_id', String(orderId));

  const raw = await http.post<AssignOrderHttpResponse & {
    data?: {
      invoice_number?: string;
      invoiceNumber?: string;
    };
  }>(
    '/system/team/order/send_invoice',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to send invoice.',
    );
  }

  return {
    message: typeof raw.message === 'string' ? raw.message : 'Invoice sent successfully.',
    invoiceNumber: raw.data?.invoice_number ?? raw.data?.invoiceNumber,
  };
}

export interface UpdateOrderPaymentStatusBody {
  orderId: number | string;
  /** 0 待支付，1 已支付，2 已退款 */
  paymentStatus: 0 | 1 | 2;
}

export interface UpdateOrderPaymentStatusResult {
  message: string;
}

/** 收款已记下，但结单失败。调用方应把订单标成已支付，以便重试关闭。 */
export class OrderCloseAfterPaymentError extends ApiRequestError {
  readonly payStatus = 1 as const;
}

/** 订单已关闭，但收款状态没有写上。调用方应把订单标成已完成、未支付。 */
export class OrderClosedBeforePaymentError extends ApiRequestError {
  readonly closed = true as const;
}

/** POST `/system/team/order/update_order_payment_status` — 团队管理员确认收款。 */
export async function postUpdateOrderPaymentStatus(
  body: UpdateOrderPaymentStatusBody,
): Promise<UpdateOrderPaymentStatusResult> {
  const formData = new FormData();
  formData.append('order_id', String(body.orderId));
  formData.append('payment_status', String(body.paymentStatus));

  const raw = await http.post<AssignOrderHttpResponse>(
    '/system/team/order/update_order_payment_status',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to update payment status.',
    );
  }
  return {
    message: paymentStatusMessage(raw.message, body.paymentStatus),
  };
}

/** 旧接口把状态码拼进文案（updated to 1）。有完整 message 时原样使用。 */
function paymentStatusMessage(message: unknown, paymentStatus: number): string {
  const text = typeof message === 'string' ? message.trim() : '';
  const legacy = /^order payment status updated to \d+$/i.test(text);
  if (text && !legacy) return text;
  if (paymentStatus === 1) return 'Payment confirmed.';
  if (paymentStatus === 2) return 'The payment has been marked as refunded.';
  if (paymentStatus === 0) return 'The order is marked as unpaid.';
  return 'Payment status updated.';
}

export interface OrderClosePaymentDetails {
  paymentMethod?: OrderPaymentMethod;
  paymentDate?: string;
}

/** 一次确认收款：先完成并关闭订单，再记下已收款。 */
export async function postConfirmInvoicePaymentAndClose(
  order: Order,
  action: InvoiceCloseAction,
  payment?: OrderClosePaymentDetails,
): Promise<{ message: string; closed: boolean; payStatus: 0 | 1 | 2 }> {
  const shouldPay = action === 'pay' || action === 'pay_and_close';
  const shouldClose = action === 'close' || action === 'pay_and_close';
  let closeMessage = '';

  if (shouldClose) {
    const hours = resolveOrderServiceDuration(order.serviceDuration, order.cleanItems) ?? 0;
    if (hours <= 0) {
      throw new ApiRequestError('Work hours are missing, so the order was not closed.');
    }

    const staff = listOrderAssignedStaff(order);
    const split = computeEqualDurationAllocations(hours, staff);
    const durationAllocations = staff
      .map((person) => ({
        operator_id: person.operatorId,
        duration: split[person.operatorId] ?? 0,
      }))
      .filter((item) => item.duration > 0);

    const closed = await postAdminCompleteOrder({
      orderId: order.id,
      serviceDuration: hours,
      durationAllocations,
      paymentMethod: payment?.paymentMethod,
      paymentDate: payment?.paymentDate,
    });
    closeMessage = closed.message;
  }

  if (!shouldPay) {
    return {
      message: closeMessage || 'Order closed.',
      closed: shouldClose,
      payStatus: order.payStatus ?? 1,
    };
  }

  try {
    const paid = await postUpdateOrderPaymentStatus({ orderId: order.id, paymentStatus: 1 });
    return {
      message: paid.message || closeMessage || 'Payment confirmed.',
      closed: shouldClose,
      payStatus: 1,
    };
  } catch (error) {
    const detail = error instanceof ApiRequestError ? error.message : 'Failed to update payment status.';
    if (shouldClose) {
      throw new OrderClosedBeforePaymentError(detail);
    }
    throw error instanceof ApiRequestError ? error : new ApiRequestError(detail);
  }
}

export interface UpdateOrderPriceBody {
  orderId: number | string;
  /** 不含 GST 总价 */
  totalPrice: number;
  /** 含 GST 总价 */
  totalPriceWithGst: number;
  serviceDuration: number;
}

export interface UpdateOrderPriceResult {
  message: string;
}

/** POST `/system/team/order/update_price` — 团队管理员修改订单价格 */
export async function postUpdateOrderPrice(
  body: UpdateOrderPriceBody,
): Promise<UpdateOrderPriceResult> {
  if (body.totalPrice <= 0 || body.totalPriceWithGst <= 0) {
    throw new ApiRequestError('Order total must be greater than 0.');
  }
  if (body.serviceDuration <= 0) {
    throw new ApiRequestError('Service duration must be greater than 0.');
  }

  const formData = new FormData();
  formData.append('order_id', String(body.orderId));
  formData.append('new_price', String(body.totalPrice));
  formData.append('total_price_with_gst', String(body.totalPriceWithGst));
  formData.append('service_duration', String(body.serviceDuration));

  const raw = await http.post<AssignOrderHttpResponse>(
    '/system/team/order/update_price',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(
      typeof raw.message === 'string' ? raw.message : 'Failed to update order price.',
    );
  }
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Order price updated.',
  };
}

/** POST `/system/team/order/mark_service_completed` — 服务人员标记服务完成 */
export async function postMarkServiceCompleted(
  body: MarkServiceCompletedBody,
): Promise<MarkServiceCompletedResult> {
  const formData = new FormData();
  formData.append('order_id', String(body.orderId));

  const raw = await http.post<AssignOrderHttpResponse & {
    data?: { all_completed?: boolean };
  }>(
    '/system/team/order/mark_service_completed',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to mark service completed.');
  }
  const payload = raw.data && typeof raw.data === 'object'
    ? (raw.data as Record<string, unknown>)
    : undefined;
  const envelope = raw as Record<string, unknown>;
  const allCompletedRaw =
    payload?.all_completed
    ?? payload?.allCompleted
    ?? envelope.all_completed
    ?? envelope.allCompleted;
  const allCompleted =
    allCompletedRaw === true
    || allCompletedRaw === 1
    || allCompletedRaw === '1'
    || allCompletedRaw === 'true';
  return {
    message: typeof raw.message === 'string' ? raw.message : 'Service marked as completed.',
    allCompleted,
  };
}

/** 一名服务人员，或接口标明全部完成，即视为服务结束。 */
export function isAssignedServiceFullyCompleted(
  order: Pick<Order, 'assignees' | 'servicers'>,
  allCompletedFlag: boolean,
): boolean {
  if (allCompletedFlag) return true;
  return listOrderAssignedStaff(order).length <= 1;
}

export type OnlineStatus = 0 | 1;

export interface SetOnlineStatusResult {
  online: OnlineStatus;
  onlineText: string;
  message: string;
}

interface SetOnlineStatusHttpResponse {
  error?: boolean;
  message?: string;
  data?: {
    online?: number;
    online_text?: string;
  };
}

/** POST `/system/team/order/set_online_status` — 团员设定在线状态（0 离线，1 在线） */
export async function postSetOnlineStatus(online: OnlineStatus): Promise<SetOnlineStatusResult> {
  const formData = new FormData();
  formData.append('online', String(online));

  const raw = await http.post<SetOnlineStatusHttpResponse>(
    '/system/team/order/set_online_status',
    formData,
    { skipApiEnvelopeModal: true },
  );
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to update online status.');
  }
  const resolvedOnline = raw.data?.online === 0 ? 0 : 1;
  return {
    online: resolvedOnline,
    onlineText:
      typeof raw.data?.online_text === 'string'
        ? raw.data.online_text
        : resolvedOnline === 1
          ? 'Online'
          : 'Offline',
    message: typeof raw.message === 'string' ? raw.message : 'Online status updated.',
  };
}

function serializeAdminCreateOrderItems(
  items: CreateOrderBody['orderItems'],
): string {
  if (!items.length) return '[]';
  return JSON.stringify(
    items.map((item) => ({
      // admin_create 后端用 team_id 作为清洁项目 ID
      team_id: item.clean_item_id,
      clean_item_id: item.clean_item_id,
      unit_price: item.unit_price,
      quantity: item.quantity,
      duration: item.duration ?? 0,
    })),
  );
}

/** POST `/system/team/order/admin_create` — 团队创建订单 */
export async function postCreateOrder(body: CreateOrderBody): Promise<CreateOrderResult> {
  const cleaningType = body.serviceType
    ? (SERVICE_TYPE_TO_CLEANING[body.serviceType] ?? 'standard')
    : 'standard';
  const { totalPrice, totalPriceWithGst } = buildAdminCreatePrices(
    body.totalPrice,
    body.gstRateDecimal,
  );
  const serviceDuration = body.serviceDuration;
  const orderItems = body.orderItems ?? [];

  if (serviceDuration <= 0) {
    throw new ApiRequestError('Service duration must be greater than 0.');
  }

  const formData = new FormData();
  formData.append('cust_name', body.custName.trim());
  formData.append('email', body.email.trim());
  formData.append('phone', body.phone?.trim() ?? '');
  formData.append('total_price', String(totalPrice));
  formData.append('total_price_with_gst', String(totalPriceWithGst));
  formData.append('service_duration', String(serviceDuration));
  formData.append('service_time', body.serviceTime);
  formData.append('access', body.access?.trim() || 'code');
  formData.append('address', body.address.trim());
  formData.append('items', serializeAdminCreateOrderItems(orderItems));
  formData.append('other_info', body.otherInfo?.trim() ?? '');
  formData.append('state', body.state);
  formData.append('street', body.street);
  formData.append('street_number', body.streetNumber);
  formData.append('postal_code', body.postalCode);
  formData.append('city', body.city);
  formData.append('cleaning_type', cleaningType);
  formData.append('payment_status', String(body.paymentStatus ?? 0));
  if (body.operatorId !== undefined && body.operatorId !== null && String(body.operatorId).trim()) {
    formData.append('operator_id', String(body.operatorId).trim());
  }

  const raw = await http.post<CreateOrderHttpResponse>('/system/team/order/admin_create', formData, {
    skipApiEnvelopeModal: true,
  });
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to create order.');
  }

  const orderId = resolveCreatedOrderId(raw.data);
  if (orderId === undefined) {
    throw new ApiRequestError('Order id missing in response.');
  }

  if (body.assigneeIds && body.assigneeIds.length > 0) {
    await postAssignOrder({ orderId, operatorIds: body.assigneeIds });
  }

  return {
    orderId,
    message: typeof raw.message === 'string' ? raw.message : 'Order created successfully.',
  };
}

export interface MemberWorkingPeriodStats {
  completedCount: number;
  inProgressCount: number;
  completedAmount: number;
}

export interface TeamMemberWorkingStats {
  operatorId: string;
  name: string;
  avatar: string;
  stats: Record<TeamOrderStatsPeriodKey, MemberWorkingPeriodStats>;
}

interface TeamWorkingStatsHttpResponse {
  error?: boolean;
  message?: string;
  data?: unknown[];
}

const EMPTY_MEMBER_PERIOD_STATS: MemberWorkingPeriodStats = {
  completedCount: 0,
  inProgressCount: 0,
  completedAmount: 0,
};

function normalizeMemberPeriodStats(raw: unknown): MemberWorkingPeriodStats {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    completedCount: readNum(o, ['completed_count', 'completedCount']) ?? 0,
    inProgressCount: readNum(o, ['in_progress_count', 'inProgressCount']) ?? 0,
    completedAmount: readNum(o, ['completed_amount', 'completedAmount']) ?? 0,
  };
}

function normalizeMemberWorkingStats(row: unknown, index: number): TeamMemberWorkingStats | null {
  if (!row || typeof row !== 'object') return null;
  const o = row as Record<string, unknown>;
  const operatorId =
    readString(o, ['operator_id', 'operatorId', 'id']) ?? `member-${index}`;
  const name = readString(o, ['name']) ?? '—';
  const avatar =
    readString(o, ['avatar']) ??
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=e8f5e9&color=1b5e20`;

  const statsRaw = o.stats;
  const statsObj = statsRaw && typeof statsRaw === 'object' ? (statsRaw as Record<string, unknown>) : {};

  return {
    operatorId,
    name,
    avatar,
    stats: {
      today: normalizeMemberPeriodStats(statsObj.today),
      week: normalizeMemberPeriodStats(statsObj.week),
      month: normalizeMemberPeriodStats(statsObj.month),
    },
  };
}

/** POST `/system/team/order/get_team_working_stats` — 团队成员工作统计。 */
export async function fetchTeamWorkingStats(): Promise<TeamMemberWorkingStats[]> {
  const raw = await http.post<TeamWorkingStatsHttpResponse>('/system/team/order/get_team_working_stats');
  if (raw.error === true) {
    throw new ApiRequestError(typeof raw.message === 'string' ? raw.message : 'Failed to load member working stats.');
  }
  const rows = Array.isArray(raw.data) ? raw.data : [];
  const members: TeamMemberWorkingStats[] = [];
  rows.forEach((row, i) => {
    const one = normalizeMemberWorkingStats(row, i);
    if (one) members.push(one);
  });
  return members;
}
